import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Instagram, Star, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";

/**
 * "Featured campers" rail — recent reviews from users who linked their
 * Instagram. Renders on the Landing page. Forms a flywheel: campers who
 * review get featured + a tag-back when we repost, which pulls them
 * deeper into the brand and brings their friends in.
 *
 * If no users have linked Instagram yet, the rail renders a friendly
 * "Be the first to be featured" CTA pointing to /profile so day-1
 * traffic still gets a meaningful, encouraging slot rather than a void.
 */
export default function FeaturedCampersRail() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null); // null = loading; [] = no items
  const [err, setErr] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/community/featured-campers", { params: { limit: 5 } });
        setItems(data?.items || []);
      } catch (_e) {
        setErr(true);
        setItems([]);
      }
    })();
  }, []);

  // Skeleton while loading — gives the page a stable layout, no jump.
  if (items === null) {
    return (
      <section className="py-16 bg-[#fafafa] border-t hairline" data-testid="featured-campers-loading">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="h-3 w-24 bg-[#eee] mb-3" />
          <div className="h-7 w-72 bg-[#eee] mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="border hairline bg-white aspect-[3/4] animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  // No reviews yet OR API errored → friendly "be the first" CTA.
  if (err || items.length === 0) {
    return (
      <section className="py-16 bg-[#fafafa] border-t hairline" data-testid="featured-campers-empty">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="eyebrow text-[#ff4500] mb-2">{t("featured.eyebrow")}</div>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight mb-4">
            Want to be on the rail?
          </h2>
          <p className="text-[15px] text-[#555] leading-relaxed max-w-2xl mb-6">
            Link your Instagram on your profile, leave an honest review of a UK campsite, and we'll
            feature you here when we spot a good one — plus tag you back when we repost it on
            <strong className="mono text-[#ff4500]"> @camp.compassuk2026</strong>.
          </p>
          <Link
            to="/profile"
            className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-xs"
            data-testid="featured-campers-cta-profile"
          >
            <Instagram className="w-3.5 h-3.5" strokeWidth={2.5} /> Link my Instagram
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-[#fafafa] border-t hairline" data-testid="featured-campers">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <header className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="eyebrow text-[#ff4500] mb-2">{t("featured.eyebrow")}</div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight">
              {t("featured.headline")}
            </h2>
          </div>
          <Link
            to="/community"
            className="text-xs mono uppercase tracking-wider text-[#ff4500] hover:underline inline-flex items-center gap-1"
            data-testid="featured-campers-all"
          >
            See all in community <ChevronRight className="w-3 h-3" />
          </Link>
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="featured-campers-grid">
          {items.map((it, i) => {
            const handle = (it.instagram_handle || "").replace(/^@/, "");
            const igUrl = handle ? `https://instagram.com/${handle}` : null;
            return (
              <article
                key={it.review_id || i}
                className="bg-white border hairline overflow-hidden flex flex-col group hover:border-[#ff4500] transition-colors"
                data-testid={`featured-camper-${i}`}
              >
                <Link to={`/campsites/${it.campsite_id}`} className="relative aspect-[4/3] overflow-hidden bg-[#fafafa]">
                  {it.campsite_image && (
                    <img
                      src={it.campsite_image}
                      alt={it.campsite_name}
                      className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                      loading="lazy"
                    />
                  )}
                  <div className="absolute top-2 left-2 bg-white/95 backdrop-blur-sm border hairline px-2 py-1 inline-flex items-center gap-1 mono text-[10px] uppercase tracking-wider">
                    <Star className="w-3 h-3 fill-[#ff4500] text-[#ff4500]" /> {it.rating?.toFixed?.(1) || it.rating}
                  </div>
                </Link>
                <div className="p-3 flex-1 flex flex-col gap-2">
                  <div className="text-[13px] font-display tracking-tight leading-tight line-clamp-1" title={it.campsite_name}>
                    {it.campsite_name}
                  </div>
                  <p className="text-xs text-[#444] leading-snug line-clamp-3 flex-1" title={it.comment}>
                    {it.comment || <span className="text-[#888] italic">(no comment)</span>}
                  </p>
                  <div className="flex items-center justify-between gap-2 pt-1.5 border-t hairline">
                    <div className="min-w-0">
                      <div className="text-[11px] mono text-[#666] truncate">{it.user_name}</div>
                      {handle && (
                        <a
                          href={igUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[11px] mono text-[#ff4500] hover:underline inline-flex items-center gap-1 truncate"
                          data-testid={`featured-camper-${i}-instagram`}
                          aria-label={`Open Instagram profile @${handle}`}
                        >
                          <Instagram className="w-3 h-3 flex-shrink-0" strokeWidth={2} />
                          @{handle}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
